import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, CheckCheck, Radio, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { apiFetch, useAuth } from '../App';
import { Link } from 'react-router-dom';

const TYPE_CONFIG = {
  SUCCESS:     { icon: CheckCircle2, color: '#10B981' },
  INFO:        { icon: Info,         color: '#3B82F6' },
  WARNING:     { icon: AlertCircle,  color: '#F59E0B' },
  LIVE_STREAM: { icon: Radio,        color: '#EF4444' },
};

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    loadNotifs();
    const interval = setInterval(loadNotifs, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadNotifs = async () => {
    try {
      const data = await apiFetch('/api/notifications');
      setNotifs(data);
      setUnread(data.filter(n => !n.read).length);
    } catch (e) {
      console.error('Failed to load notifications:', e);
    }
  };

  const markRead = async (id) => {
    try {
      await apiFetch(`/api/notifications/${id}/read`, { method: 'PATCH' });
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Failed to mark notification as read:', e);
    }
  };

  const markAllRead = async () => {
    try {
      await apiFetch('/api/notifications/read-all', { method: 'POST' });
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) {
      console.error('Failed to mark all notifications as read:', e);
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        data-testid="notif-bell"
      >
        <Bell size={20} style={{ color: 'var(--text-primary)' }} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-white text-xs flex items-center justify-center font-bold"
            style={{ background: '#EF4444', fontSize: 10 }}
          >{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 rounded-2xl shadow-2xl overflow-hidden z-50"
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
          data-testid="notif-panel"
        >
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
            <span className="font-outfit font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Notifications</span>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs flex items-center gap-1" style={{ color: 'var(--petfolk-orange)' }}>
                  <CheckCheck size={12} /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} style={{ color: 'var(--text-secondary)' }}><X size={14} /></button>
            </div>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: 360 }}>
            {notifs.length === 0 ? (
              <div className="py-8 text-center">
                <Bell size={28} className="mx-auto mb-2" style={{ color: 'var(--text-secondary)', opacity: 0.3 }} />
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No notifications yet</p>
              </div>
            ) : (
              notifs.map(n => {
                const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.INFO;
                const Icon = cfg.icon;
                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-black/3 dark:hover:bg-white/3 transition-colors ${!n.read ? 'bg-petfolk-orange/5' : ''}`}
                    style={{ borderBottom: '1px solid var(--border-color)' }}
                    onClick={() => markRead(n.id)}
                    data-testid={`notif-${n.id}`}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${cfg.color}18` }}>
                      <Icon size={14} style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{n.title}</p>
                      <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{n.body}</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)', opacity: 0.6 }}>
                        {new Date(n.createdAt).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full mt-2 shrink-0" style={{ background: 'var(--petfolk-orange)' }} />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
